import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getTasks,
  getNotifications,
  getAssignments,
  getDocuments,
} from '../service';

/**
 * Prefetches dashboard data in parallel after auth completes.
 * Eliminates the waterfall where tasks/notifications wait for component mount.
 */
const usePrefetchDashboard = (isAuthenticated) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    // Fire parallel prefetch burst for data the dashboard will need.
    // Profile and classData are already fetching via their hooks in App.jsx.
    // These additional prefetches run concurrently so everything is ready
    // by the time the route components mount.
    //
    // IMPORTANT: react-query invokes queryFn with a QueryFunctionContext arg
    // ({ queryKey, signal, meta }). Service functions like getAssignments
    // forward their first arg into the URL (subjectCode), so passing the
    // raw reference produces "/assignments?subjectCode=[object Object]"
    // and a wasted request. Wrap each call so the context is dropped.
    queryClient.prefetchQuery({
      queryKey: ['tasks'],
      queryFn: () => getTasks(),
      staleTime: 60_000,
    });
    queryClient.prefetchQuery({
      queryKey: ['notifications'],
      queryFn: () => getNotifications(),
      staleTime: 60_000,
    });
    queryClient.prefetchQuery({
      queryKey: ['assignments'],
      queryFn: () => getAssignments(),
      staleTime: 60_000,
    });
    queryClient.prefetchQuery({
      queryKey: ['allDocuments'],
      queryFn: () => getDocuments(),
      staleTime: 60_000,
    });
  }, [isAuthenticated, queryClient]);
};

export default usePrefetchDashboard;
